import * as React from 'react';
import { useDispatch } from 'react-redux';
import { isUndefined, noop } from 'underscore';
import FontAwesome from 'components/FontAwesome';
import { Watermark } from 'components/icons';
import useModalMonitor from 'containers/ConnectedModal/useModalMonitor';
import { AddLogoModal, EditLogoModal } from 'containers/LogoModal';
import ProIndicator, { ProIndicatorOverlay } from 'containers/ProIndicator';
import { FeatureLockState, UNLOCKED } from 'redux/modules/embed/selectors';
import { pushModal } from 'redux/modules/modal';
import { ModalName } from 'redux/modules/modal/types';
import { ProjectTypeByAssets } from 'utils/constants';
import StyleTileButton from '../../components/StyleTileButton';
import { block } from '../../utils';
import LockedWatermarkEditModal from '../LockedWatermarkEditModal';
import LockedWatermarkSelectModal from '../LockedWatermarkSelectModal';
import WatermarkTooltip from './WatermarkTooltip';

export interface WatermarkTileProps {
  className?: string;
  hasOverrides: boolean;
  imageAtPosition: string;
  lockState?: FeatureLockState;
  logoImage?: string;
  logoStatus: 'adding' | 'deleting';
  onModalHide?: () => void;
  onModalShow?: () => void;
  positionSec: number;
  previewBackgroundImage?: string;
  projectType: ProjectTypeByAssets;
}

const MODAL_NAMES: ModalName[] = ['EditWatermark', 'AddWatermark'];

const WatermarkTile: React.SFC<WatermarkTileProps> = props => {
  const {
    hasOverrides,
    lockState = UNLOCKED,
    logoImage,
    logoStatus,
    onModalShow = noop,
    onModalHide = noop,
    projectType,
  } = props;
  const { isLocked } = lockState;
  const isNoAssets = projectType === ProjectTypeByAssets.PROJECT_NO_ASSETS;
  const dispatch = useDispatch();
  useModalMonitor({
    names: MODAL_NAMES,
    onHide: onModalHide,
    onShow: onModalShow,
  });
  const handleLogoButtonClick = () => {
    if (hasLogo()) {
      dispatch(
        pushModal({
          name: 'EditWatermark',
        }),
      );
    } else {
      dispatch(
        pushModal({
          name: 'AddWatermark',
        }),
      );
    }
  };

  const handleUpgradeClick = (event: React.MouseEvent) => {
    event.preventDefault();
    dispatch(
      pushModal({
        name: 'WatermarkUpsell',
      }),
    );
  };

  /**
   * whether or not a logo exists - i.e. determines if we show the "add" modal or "delete" modal.
   *
   * note that if there is no logo url but the logo status is "deleting", we still consider this
   * as having a logo (meaning this method returns true). this prevents the "delete" button from
   * turning back into an "upload" button prematurely
   */
  const hasLogo = () => {
    return (
      !isUndefined(logoImage) ||
      (isUndefined(logoImage) && logoStatus === 'deleting')
    );
  };

  const showWatermarkButtons = () => {
    return hasLogo() || projectType !== ProjectTypeByAssets.PROJECT_NO_ASSETS;
  };

  return (
    <>
      <ProIndicator className={block('badge')} feature="customWatermark">
        {({ isFree, badge }) => {
          // Hard click block — only the lockSpec lock actually swallows the
          // click.  Admin lock keeps click-through (so the overrides flow
          // can open the LockedWatermark modal) and no-assets/pro-gated
          // dim the tile but don't block.
          const blockClicks = lockState.disabled;
          // CSS fade modifier.  Skipped when IconButton's own [disabled]
          // styling dims the button — otherwise the opacities stack
          // (0.5 × 0.3 = 0.15, way too faint).
          const fadeTile = (isLocked || isNoAssets || isFree) && !blockClicks;
          // Hover-tooltip visibility.  Suppressed when pro-gated because
          // the ProIndicatorOverlay supplies its own upsell UX.
          const showTooltip = (isLocked || isNoAssets) && !isFree;

          return (
            <WatermarkTooltip show={showTooltip}>
              <StyleTileButton
                className={block('tile', {
                  disabled: fadeTile,
                  inside: true,
                })}
                disabled={blockClicks}
                icon={
                  <Watermark
                    className={block('watermark-icon', {
                      faded: fadeTile,
                    })}
                    color={fadeTile ? '#555c66' : '#6e6ede'}
                  />
                }
                label={hasLogo() ? 'Edit Watermark' : 'Add Watermark'}
                onClick={handleLogoButtonClick}
                badge={
                  isLocked && (
                    <FontAwesome
                      className={block('tile-button-lock')}
                      icon="lock"
                    />
                  )
                }
              />
              {!isLocked && badge}
              {!isLocked && isFree && (
                <ProIndicatorOverlay
                  from="Add Watermark"
                  onClick={handleUpgradeClick}
                />
              )}
            </WatermarkTooltip>
          );
        }}
      </ProIndicator>

      {showWatermarkButtons() &&
        (hasOverrides ? (
          <>
            <LockedWatermarkSelectModal />
            <LockedWatermarkEditModal />
          </>
        ) : (
          <>
            <AddLogoModal />
            <EditLogoModal showDeleteButton logoImageSrc={logoImage} />
          </>
        ))}
    </>
  );
};

export default WatermarkTile;
